import { useCallback, useEffect, useId, useRef, useState } from 'react'

import { FilterAccordion, RadioField, useOnClickOutside } from '@avantstay/backoffice-core'

import { RangeFilter } from '@new/__global/components/RangeFilter'

type Rating = {
  from?: string
  to?: string
}

type Option<Value extends string> = {
  id: string
  title: string
  value: Value
  showRatingFilter?: boolean
}

interface RadioRatingProps<Value extends string> {
  title: string
  options: Option<Value>[]
  initialOptionValue?: Value
  initialRange?: Rating
  ratingRange?: { min?: number; max?: number }
  radioGroupId?: string
  isNegativeValueAllowed?: boolean
  onChange: (value?: { option: Value; rating?: Rating }) => void
}

export const RadioRatingFilter = <Value extends string>({
  title,
  options,
  initialOptionValue,
  initialRange,
  ratingRange,
  radioGroupId,
  isNegativeValueAllowed,
  onChange,
  ...props
}: RadioRatingProps<Value>) => {
  const randomRadioGroupId = useId()

  const [selectedOptionValue, setSelectedOptionValue] = useState(() => initialOptionValue)
  const [selectedRating, setSelectedRating] = useState(() => initialRange)

  const wrapperRef = useRef<HTMLDivElement>(null)

  const selectedOption = options.find(({ value }) => value === selectedOptionValue)

  const resetSelectedValues = useCallback(() => {
    setSelectedOptionValue(undefined)
    setSelectedRating(undefined)
  }, [])

  const handleSubmitValue = () => {
    if (!selectedOption) {
      onChange()
      return
    }

    if (!selectedOption.showRatingFilter || !selectedRating) {
      onChange({ option: selectedOption.value })
      return
    }

    const isGreaterThanMinRange =
      ratingRange?.min !== undefined && Number(ratingRange.min) <= Number(selectedRating.from ?? 0)
    const isLessThanMaxRange =
      ratingRange?.max !== undefined && Number(ratingRange.max) >= Number(selectedRating.to ?? 0)

    const isWithinRange = isGreaterThanMinRange && isLessThanMaxRange
    const isValidRange =
      selectedRating.to && selectedRating.from
        ? Number(selectedRating.to) >= Number(selectedRating.from)
        : true

    if (isWithinRange && isValidRange) {
      onChange({
        option: selectedOption.value,
        rating: {
          from: selectedRating.to ? selectedRating.from || '0' : selectedRating.from,
          to: selectedRating.to,
        },
      })
    }
  }

  useEffect(() => {
    const hasResetFilters = !initialOptionValue && !initialRange?.from && !initialRange?.to

    if (hasResetFilters) {
      resetSelectedValues()
    }
  }, [initialOptionValue, initialRange?.from, initialRange?.to, resetSelectedValues])

  useOnClickOutside(wrapperRef, handleSubmitValue)

  return (
    <FilterAccordion
      ref={wrapperRef}
      title={title}
      filterValue={`${selectedRating?.from ?? ''} ${selectedRating?.to ?? ''}`}
      onClearFilter={resetSelectedValues}
      {...props}
    >
      <RadioField.Group
        label={radioGroupId ?? randomRadioGroupId}
        options={options}
        value={selectedOptionValue}
        onChange={e => setSelectedOptionValue(e.currentTarget.value as Value)}
      />

      {selectedOption?.showRatingFilter ? (
        <RangeFilter
          shouldDisplayFiltersOnSameLine
          fromValue={selectedRating?.from}
          toValue={selectedRating?.to}
          minFromValue={ratingRange?.min}
          maxToValue={ratingRange?.max}
          isNegativeValueAllowed={isNegativeValueAllowed}
          onChangeFromValue={event => {
            setSelectedRating(prev => ({ ...prev, from: event.target?.value }))
          }}
          onChangeToValue={event => {
            setSelectedRating(prev => ({ ...prev, to: event.target?.value }))
          }}
        />
      ) : null}
    </FilterAccordion>
  )
}
